'use client';

import { type FC, forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  Rectangle,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';

import type { BarChartClickHandler, Data } from '@/components/charts/types';
import { CardDescription, CardTitle } from '@/components/ui/card';
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/components/ui/chart';
import { useComposition } from '@/hooks/use-composition';
import { cn } from '@/lib/utils';

interface BarChartProps {
  title: string;
  description?: string;
  config?: ChartConfig;
  data: Array<Data>;
  multiple?: boolean;
  xAxis: string;
  className?: string;
  children?: React.ReactNode;
  handleClick?: BarChartClickHandler;
}

type BarChartLegendProps = React.HTMLAttributes<HTMLOrSVGElement>;
const BarChartLegend = forwardRef<HTMLSlotElement, BarChartLegendProps>(
  (props, ref) => (
    <Slot
      data-legend
      ref={ref}
      {...props}
    />
  )
);
BarChartLegend.displayName = 'BarChartLegend';

const BarChart: FC<BarChartProps> = ({
  title,
  description,
  config = {},
  data,
  xAxis,
  multiple = false,
  className,
  children: _children,
  handleClick,
}) => {
  const [_, legends] = useComposition(_children, BarChartLegend.displayName);

  return (
    <div
      data-chart
      className={cn('flex h-full flex-col justify-between gap-2', className)}>
      <div className='flex flex-col gap-1'>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </div>
      <div className='relative'>
        <ResponsiveContainer
          width={400}
          height={'30%'}>
          <ChartContainer config={config}>
            <RechartsBarChart
              accessibilityLayer
              data={data}>
              <CartesianGrid vertical={false} />
              <XAxis
                tick={false}
                dataKey={xAxis}
                tickLine={false}
                tickMargin={10}
                axisLine={false}
              />
              <YAxis
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) => value.toFixed(2)}
              />
              <ChartTooltip
                cursor={true}
                content={<ChartTooltipContent indicator='dashed' />}
              />
              {multiple ? (
                Object.entries(config).map(([key, value]) => (
                  <Bar
                    key={key}
                    dataKey={key}
                    fill={value.color}
                    radius={4}
                  />
                ))
              ) : (
                <Bar
                  dataKey={xAxis}
                  radius={4}
                  onClick={handleClick}
                  activeBar={({ ...props }) => (
                    <Rectangle
                      {...props}
                      fillOpacity={0.8}
                      // eslint-disable-next-line react/prop-types
                      stroke={props.payload.fill as string}
                    />
                  )}
                />
              )}
            </RechartsBarChart>
          </ChartContainer>
        </ResponsiveContainer>
      </div>
      {legends}
    </div>
  );
};

interface BarChartComposition {
  Legend: typeof BarChartLegend;
}
const RootWithComposition: typeof BarChart & BarChartComposition =
  Object.assign(BarChart, {
    Legend: BarChartLegend,
  });

export { RootWithComposition as BarChart };
